import React, { useEffect, useState } from "react";
import { Col, DatePicker, Form, Input, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import dayjs from "dayjs";
import { DraggableModal } from "../../../components/DraggableModal";
import { EntReservoirFilter } from "../../../models/filters/reservoirFilter";
import { EntReservoir } from "../../../models/reservoir";
import { useGetReservoirListQuery } from "../../../redux/api/reservoir/reservoirApi";
import { useTranslation } from "react-i18next";

interface IProps {
    show: boolean;
    reservoirHistory: any;
    onHide: () => void;
    onSubmit: (pos: any) => Promise<boolean>;
}

export const AddReservoirHistoryModal = (props: IProps) => {
    const { data: reservoirs } = useGetReservoirListQuery(new EntReservoirFilter());
    const [form] = useForm();
    const {t} = useTranslation()
    const [reservoirsList, setReservoirsList] = useState<EntReservoir[] | null>(null);
    const [changedFields, setChangedFields] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        reservoirs?.res?.content && setReservoirsList(reservoirs.res.content);
    }, [reservoirs]);

    useEffect(() => {
        if (props.show) setFieldsFromEntity();
    }, [props.show]);

    const setFieldsFromEntity = (): void => {
        if (props.reservoirHistory) {
            form.setFieldsValue({dateForDay: props.reservoirHistory.dateForDay ? dayjs(props.reservoirHistory.dateForDay) : null,  });

            const fieldValues: Record<string, any> = {};
            if (props.reservoirHistory.reservoirs) {
                Object.values(props.reservoirHistory.reservoirs).forEach((record: any) => {
                    if (record?.reservoir) {
                        fieldValues[`volume_${record.reservoir.id}`] = record.volume;
                        fieldValues[`admission_${record.reservoir.id}`] = record.admission;
                        fieldValues[`workOff_${record.reservoir.id}`] = record.workOff;
                    }
                });
            }

            form.setFieldsValue(fieldValues);
            setChangedFields({}); // Сбрасываем измененные поля при загрузке
        }
    };

    const handleChange = (fieldName: string, value: any) => {
        setChangedFields((prev) => ({ ...prev, [fieldName]: true })); // Помечаем поле как измененное
    };

    const handleHide = () => {
        form.resetFields();
        props.onHide();
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await form.validateFields();
            const payload = reservoirsList?.map((reservoir) => ({
                id: props.reservoirHistory?.id,
                volume: form.getFieldValue(`volume_${reservoir.id}`),
                admission: form.getFieldValue(`admission_${reservoir.id}`),
                workOff: form.getFieldValue(`workOff_${reservoir.id}`),
                reservoirId: reservoir.id,
                dateForDay: form.getFieldValue("dateForDay")?.format("YYYY-MM-DD"),
            }));

            const result = await props.onSubmit(payload as any);
            if (result) handleHide();
        } finally {
            setLoading(false);
        }
    };

    return (
        <DraggableModal
            title={
                props.reservoirHistory.id === null
                    ? t('general.add')
                    : `${t('reservoir_history.edit_date')} ${props.reservoirHistory.dateForDay}`
            }
            visible={props.show}
            onCancel={handleHide}
            onSubmit={handleSubmit}
            submitLoading={loading}
            width={800}
        >
            <Form
                initialValues={{ remember: true }}
                autoComplete="off"
                form={form}
                layout="vertical"
            >

                {props.reservoirHistory.id === null ?
                 <Form.Item
                 label={ t('reservoir_history.date_for_day') }
                 name="dateForDay"
                 rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
             >
                 <DatePicker placeholder={t('general.select_date')} disabledDate={(current) => current && current > dayjs().endOf('day')} />
             </Form.Item>: ""
                
                }
               

                {reservoirsList &&
                    reservoirsList.map((element) => (
                        <Row key={element.id} gutter={10}>
                            <Col span={6}>
                                <h4>{element.nameRu}</h4>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label={ t('reservoir_history.volume') }
                                    name={`volume_${element.id}`}
                                    rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
                                >
                                    <Input
                                        onChange={(e) => handleChange(`volume_${element.id}`, e.target.value)}
                                        style={{
                                            borderColor: changedFields[`volume_${element.id}`] ? "#95dc95" : undefined,
                                        }}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label={ t('reservoir_history.admission') }
                                    name={`admission_${element.id}`}
                                    rules={[{ required: true, message: t('msg.fill_in_the_field')}]}
                                >
                                    <Input
                                        onChange={(e) => handleChange(`admission_${element.id}`, e.target.value)}
                                        style={{
                                            borderColor: changedFields[`admission_${element.id}`] ? "#95dc95" : undefined,
                                        }}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label={ t('reservoir_history.work_off') }
                                    name={`workOff_${element.id}`}
                                    rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
                                >
                                    <Input
                                        onChange={(e) => handleChange(`workOff_${element.id}`, e.target.value)}
                                        style={{
                                            borderColor: changedFields[`workOff_${element.id}`] ? "#95dc95" : undefined,
                                        }}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    ))}
            </Form>
        </DraggableModal>
    );
};
